import {
  MetricCard,
  type MetricCardVariant,
} from '@data/components/composite/Metrics/MetricCard/MetricCard';
import type { MetricV2Response } from '@data/config/metrics';
import { cn } from '@data/utils/cn/cn';

interface MetricsProps {
  metrics: MetricV2Response[];
  isLoading?: boolean;
  count?: number;
  variant?: MetricCardVariant;
}

export function Metrics({ metrics, isLoading, count, variant }: MetricsProps) {
  const total = isLoading ? count ?? 0 : metrics.length;
  const isSecondary = variant === 'secondary';

  const numRows = isSecondary && total > 3 ? 2 : 1;
  function getPrimaryCols() {
    if (total === 4) return 4;
    return total % 3 === 0 ? 3 : 2;
  }
  const numCols = isSecondary ? Math.ceil(total / numRows) : getPrimaryCols();

  // Use static class strings so Tailwind can detect them
  const gridColsByCount: Record<number, string> = {
    2: '@[523px]:grid-cols-2',
    3: '@[523px]:grid-cols-3',
    // Four abreast needs ~900px to keep the longest metric label on one line,
    // so they pair up between the breakpoints rather than crowding.
    4: '@[523px]:grid-cols-2 @[900px]:grid-cols-4',
  };
  const gridCols = gridColsByCount[numCols] ?? '@[523px]:grid-cols-2';

  // Primary: span last item across 2 cols when odd count not divisible by 3
  const getSpanClassName = (index: number) =>
    !isSecondary && total % 3 !== 0 && total % 2 !== 0 && index === total - 1
      ? '@[523px]:col-span-2'
      : '';

  // ── Secondary: column-wrapper layout ─────────────────────────────────────────
  if (isSecondary) {
    const items = isLoading
      ? Array.from({ length: total }, (_, i) => ({
          key: String(i),
          isLoading: true as const,
        }))
      : metrics.map((m) => ({ key: m.label, metric: m, isLoading: false }));

    // Chunk items into columns of numRows each
    const columns = Array.from({ length: numCols }, (_, colIdx) =>
      items.slice(colIdx * numRows, colIdx * numRows + numRows)
    );

    return (
      <div className="mt-6 @container">
        <div className={cn('grid grid-cols-1', gridCols)}>
          {columns.map((colItems, colIdx) => {
            const isLastCol = colIdx === numCols - 1;
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={colIdx}
                className={cn(
                  'border-neutral-200 @[523px]:mb-0',
                  !isLastCol &&
                    'mb-4 border-b @[523px]:border-b-0 @[523px]:border-r'
                )}
              >
                {colItems.map((item) =>
                  item.isLoading ? (
                    <MetricCard
                      key={item.key}
                      value={null}
                      label=""
                      variant={variant}
                      isLoading
                    />
                  ) : (
                    <MetricCard
                      key={item.key}
                      value={item.metric.value}
                      label={item.metric.label}
                      popoverContent={item.metric.toolTip}
                      format={item.metric.format}
                      variant={variant}
                    />
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Primary: flat grid layout ────────────────────────────────────────────────
  return (
    <div className="@container">
      <div className={cn('grid grid-cols-1 gap-3', gridCols)}>
        {isLoading
          ? Array.from({ length: total }).map((_, index) => (
              <MetricCard
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                value={null}
                label=""
                className={getSpanClassName(index)}
                variant={variant}
                isLoading
              />
            ))
          : metrics.map((metric, index) => (
              <MetricCard
                key={metric.label}
                value={metric.value}
                label={metric.label}
                popoverContent={metric.toolTip}
                format={metric.format}
                dateOfAssessment={metric.dateOfAssessment}
                className={getSpanClassName(index)}
                variant={variant}
              />
            ))}
      </div>
    </div>
  );
}
